import { useCallback, useSyncExternalStore } from "react";

export type Theme = "dark" | "light";

const STORAGE_KEY = "elena-theme";
const listeners = new Set<() => void>();

// index.html sets data-theme from localStorage before first paint;
// absence of the attribute means the dark default.
function current(): Theme {
  return document.documentElement.dataset.theme === "light" ? "light" : "dark";
}

export function setTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme;
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* private mode — theme just won't persist */
  }
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Current theme + toggle. Subscribing components re-render on change. */
export function useTheme(): [Theme, () => void] {
  const theme = useSyncExternalStore(subscribe, current);
  const toggle = useCallback(() => setTheme(current() === "dark" ? "light" : "dark"), []);
  return [theme, toggle];
}

function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/** Chart.js can't read CSS variables — resolve the active palette at render time. */
export function chartColors() {
  return {
    grid: cssVar("--chart-grid"),
    ticks: cssVar("--text-tertiary"),
    legend: cssVar("--text-secondary"),
    surface: cssVar("--surface"),
    accent: cssVar("--accent"),
    terracotta: cssVar("--terracotta"),
    success: cssVar("--success"),
    warning: cssVar("--warning"),
    error: cssVar("--error"),
  };
}
